import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, inr, resolveImg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import PhotoUploader from "../components/PhotoUploader";
import Countdown from "../components/Countdown";
import { toast } from "sonner";
import { BidzWallet } from "../components/BidzWallet";
import { AlertCircle, CheckCircle2, ShieldAlert } from "lucide-react";

const DISPUTE_REASONS = [
  { v: "not_received", l: "Item not received" },
  { v: "damaged", l: "Item arrived damaged" },
  { v: "wrong_item", l: "Wrong item sent" },
  { v: "materially_different", l: "Materially different from listing" },
  { v: "missing_parts", l: "Missing parts / accessories" },
  { v: "authenticity_concern", l: "Authenticity concern" },
  { v: "shipping_problem", l: "Shipping problem" },
  { v: "other", l: "Other" },
];

export default function BuyerDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [watchlist, setWatchlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [disputeOrder, setDisputeOrder] = useState(null);
  const [disputeForm, setDisputeForm] = useState({ reason: "damaged", description: "", evidence: [] });

  useEffect(() => { if (!loading && !user) nav("/signin?next=/buyer"); }, [user, loading, nav]);
  useEffect(() => {
    if (!user) return;
    api.get("/watchlist").then(r => setWatchlist(r.data));
    api.get("/orders/mine", { params: { role: "buyer" } }).then(r => setOrders(r.data));
    api.get("/disputes/mine", { params: { role: "buyer" } }).then(r => setDisputes(r.data));
  }, [user]);

  const reload = () => {
    api.get("/orders/mine", { params: { role: "buyer" } }).then(r => setOrders(r.data));
    api.get("/disputes/mine", { params: { role: "buyer" } }).then(r => setDisputes(r.data));
  };

  const createOrder = async (listingId) => {
    try {
      const r = await api.post(`/orders/from-listing/${listingId}`);
      toast.success("Order created"); nav(`/checkout/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot create order"); }
  };

  const acceptOrder = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/accept`);
      toast.success("Item accepted", { description: "Seller payout released from escrow." });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot accept"); }
  };

  const submitDispute = async () => {
    try {
      await api.post(`/orders/${disputeOrder.id}/dispute`, disputeForm);
      toast.success("Dispute opened", { description: "Payout has been placed on hold." });
      setDisputeOrder(null);
      setDisputeForm({ reason: "damaged", description: "", evidence: [] });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open dispute"); }
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Buyer Dashboard</div>
      <h1 className="text-3xl md:text-4xl font-extrabold">Your bids & orders</h1>

      <Tabs defaultValue={params.get("tab") === "bidz" ? "bidz" : "watchlist"} className="mt-8">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="bidz" data-testid="buyer-tab-bidz">Bidz Wallet</TabsTrigger>
          <TabsTrigger value="watchlist" data-testid="buyer-dashboard-watchlist-tab">Watchlist ({watchlist.length})</TabsTrigger>
          <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="buyer-tab-disputes">Disputes ({disputes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="bidz" className="mt-6">
          <BidzWallet />
        </TabsContent>

        <TabsContent value="watchlist" className="mt-6">
          {watchlist.length === 0 ? <Empty msg="Your watchlist is empty." /> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {watchlist.map(l => (
                <div key={l.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <Link to={`/auction/${l.id}`} className="flex gap-3">
                    {l.photos?.[0] && <img src={l.photos[0]} className="h-20 w-20 rounded-lg object-cover" alt="" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-amber-600 uppercase font-bold">{l.category_name}</div>
                      <div className="font-semibold line-clamp-1">{l.title}</div>
                      <div className="text-lg font-extrabold mt-1">{inr(l.current_bid || l.starting_bid)}</div>
                      <Countdown endsAt={l.ends_at} small />
                    </div>
                  </Link>
                  {l.highest_bidder_id === user.id && new Date(l.ends_at) < new Date() && (
                    <Button size="sm" onClick={() => createOrder(l.id)} className="mt-3 w-full bg-amber-500 text-slate-900 font-bold">You won — Checkout</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? <Empty msg="No orders yet." /> : (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
                  <tr><th className="px-4 py-3">Item</th><th className="px-4 py-3">Winning bid</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td className="px-4 py-3 font-semibold">{o.listing_title}</td>
                      <td className="px-4 py-3">{inr(o.winning_bid)}</td>
                      <td className="px-4 py-3 font-bold">{inr(o.buyer_total)}</td>
                      <td className="px-4 py-3 text-xs uppercase font-bold">{o.status.replace(/_/g, " ")}</td>
                      <td className="px-4 py-3 flex gap-2">
                        {o.status === "payment_pending" && <Link to={`/checkout/${o.id}`} className="text-amber-600 font-semibold text-sm">Pay now →</Link>}
                        {(o.status === "inspection_period" || o.status === "delivered") && (
                          <>
                            <Button size="sm" onClick={() => acceptOrder(o.id)} data-testid={`accept-${o.id}`} className="bg-emerald-600 text-white h-8 gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Accept
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setDisputeOrder(o)} data-testid={`dispute-${o.id}`} className="h-8 gap-1 text-red-600 border-red-200">
                              <ShieldAlert className="h-3.5 w-3.5" /> Report issue
                            </Button>
                          </>
                        )}
                        {o.status === "dispute_open" && <span className="text-red-600 text-xs font-bold">Under review</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="disputes" className="mt-6">
          {disputes.length === 0 ? <Empty msg="No disputes." /> : (
            <div className="space-y-3" data-testid="buyer-disputes-list">
              {disputes.map(d => (
                <div key={d.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase font-bold text-amber-600 tracking-wider">Case #{d.id.slice(0,8)}</div>
                      <div className="font-bold mt-1">{d.listing_title}</div>
                      <div className="text-xs text-slate-500 mt-1">Reason: {d.reason.replace(/_/g," ")} · Seller: {d.seller_name}</div>
                      <div className="mt-2 text-sm">{d.description}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                      d.status === "resolved" ? "bg-emerald-100 text-emerald-700"
                      : d.status === "cancelled" ? "bg-slate-100 text-slate-600"
                      : "bg-amber-100 text-amber-700"
                    }`}>{d.status.replace(/_/g," ")}</span>
                  </div>
                  {d.resolution && <div className="mt-2 text-xs text-slate-600 dark:text-slate-400"><strong>Resolution:</strong> {d.resolution.replace(/_/g," ")}{d.resolution_note ? ` — ${d.resolution_note}` : ""}</div>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!disputeOrder} onOpenChange={o => !o && setDisputeOrder(null)}>
        <DialogContent data-testid="dispute-modal" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-red-600" /> Report an issue</DialogTitle>
          </DialogHeader>
          {disputeOrder && (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">Order: <strong>{disputeOrder.listing_title}</strong></div>
              <div>
                <Label>Reason</Label>
                <Select value={disputeForm.reason} onValueChange={v => setDisputeForm(f => ({ ...f, reason: v }))}>
                  <SelectTrigger data-testid="dispute-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISPUTE_REASONS.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Describe the issue</Label>
                <Textarea data-testid="dispute-description" rows={4} value={disputeForm.description}
                  onChange={e => setDisputeForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe what happened, when, and any relevant details..." />
              </div>
              <div>
                <Label>Evidence (photos)</Label>
                <PhotoUploader photos={disputeForm.evidence}
                  onChange={list => setDisputeForm(f => ({ ...f, evidence: list }))}
                  max={6} />
              </div>
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-300">
                Opening a dispute places the seller's payout on hold. Our team reviews cases based on evidence and policy — do not accuse users; describe facts.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeOrder(null)}>Cancel</Button>
            <Button data-testid="dispute-submit" onClick={submitDispute} disabled={!disputeForm.description}
              className="bg-red-600 hover:bg-red-500 text-white">Open dispute</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Empty({ msg }) {
  return <div className="text-center text-slate-500 py-16">{msg}</div>;
}
